import { forwardRef } from 'react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import {
    baselineOffset,
    useFitFontSize,
    VALUE_INK_SHADOW,
    VALUE_LINE_BOX,
} from '../shared/formFieldEmphasis';
import { FAMILY_CARD_FONT_FAMILY } from '../family-card-form/familyCardFont';
import { buildFuelCardLetter } from './fuelCardFormEngine';

export const FUEL_CARD_PAGE_WIDTH = 595;
export const FUEL_CARD_PAGE_HEIGHT = 842;

const BODY_FONT_PX = 12;
/**
 * Typed answers print larger and heavier than the letter's own text so they
 * stay legible on a photocopy — the same treatment as the family card and the
 * other government forms. One that outgrows its line falls back toward the body
 * size rather than being clipped.
 */
const VALUE_FONT_PX = 14;
const FIELD_LINE_HEIGHT_PX = 22;
/** Answers start a little in from the label and ride just above the dots. */
const VALUE_INSET = 6;
const VALUE_LIFT = 7;

/**
 * The dotted line is an empty box with a bottom border, so its own baseline —
 * and therefore the label's — sits on the dots; its border box is this tall.
 */
const DOT_LINE_PAD = 3;
const DOT_LINE_BOTTOM = DOT_LINE_PAD + 1;
/** …and it is nudged this far under the label's baseline, as ruled paper is. */
const DOT_LINE_DROP = 2;

/** An answer is placed by its baseline, `VALUE_LIFT` above the dotted line. */
const valueTop = (size) => (
    DOT_LINE_BOTTOM - VALUE_LIFT - baselineOffset(size, VALUE_LINE_BOX * size)
);
const ROW_GAP = 6;
const BLOCK_GAP = 4;
const TITLE_BOTTOM_GAP_PX = 20;

const PAGE_STYLE = {
    width: FUEL_CARD_PAGE_WIDTH,
    minHeight: FUEL_CARD_PAGE_HEIGHT,
    background: '#fff',
    color: '#111',
    padding: '36px 42px 40px',
    boxSizing: 'border-box',
    fontFamily: FAMILY_CARD_FONT_FAMILY,
    fontSize: BODY_FONT_PX,
    lineHeight: 1.5,
    position: 'relative',
    overflow: 'visible',
};

const PHOTO_BOX = {
    position: 'absolute',
    top: 36,
    right: 42,
    width: 108,
    height: 132,
    border: '2px dashed #666',
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 6,
    boxSizing: 'border-box',
    background: '#fff',
};

const FIELD_FONT = {
    fontSize: BODY_FONT_PX,
    fontFamily: 'inherit',
    lineHeight: `${FIELD_LINE_HEIGHT_PX}px`,
};

function FieldLine({ label, value }) {
    const hasValue = Boolean(value);
    const [valueRef, valueSize] = useFitFontSize(VALUE_FONT_PX, BODY_FONT_PX, [value]);
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 4,
                width: '100%',
                marginBottom: ROW_GAP,
            }}
        >
            <span
                className="fuel-card-field-label"
                style={{ ...FIELD_FONT, flexShrink: 0, whiteSpace: 'nowrap', paddingBottom: 3 }}
            >
                {label}
            </span>
            {/* The dotted line runs the whole blank whether or not it is filled,
                and the answer sits on top of it, as on a hand-written application. */}
            <span
                data-field-line
                style={{
                    flex: 1,
                    minWidth: 32,
                    position: 'relative',
                    top: DOT_LINE_DROP,
                    paddingBottom: DOT_LINE_PAD,
                    borderBottom: '1px dotted #222',
                }}
            >
                {hasValue ? (
                    <span
                        ref={valueRef}
                        data-field-value
                        data-filled="true"
                        className="fuel-card-field-value"
                        style={{
                            fontFamily: 'inherit',
                            fontSize: valueSize,
                            lineHeight: VALUE_LINE_BOX,
                            position: 'absolute',
                            left: VALUE_INSET,
                            top: valueTop(valueSize),
                            maxWidth: `calc(100% - ${VALUE_INSET * 2}px)`,
                            fontWeight: 400,
                            textShadow: VALUE_INK_SHADOW,
                            color: '#111',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {value}
                    </span>
                ) : null}
            </span>
        </div>
    );
}

function NumberedBlock({ number, children }) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 6,
                marginBottom: BLOCK_GAP,
            }}
        >
            <span
                style={{ flexShrink: 0, width: 22, lineHeight: `${FIELD_LINE_HEIGHT_PX}px`, fontSize: BODY_FONT_PX }}
            >
                {number}।
            </span>
            <div style={{ flex: 1, minWidth: 0, paddingRight: 100 }}>{children}</div>
        </div>
    );
}

function OfficialDottedLine({ label, noMargin = false }) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 4,
                width: '100%',
                marginBottom: noMargin ? 0 : 12,
            }}
        >
            <span
                style={{
                    ...FIELD_FONT,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    paddingBottom: 3,
                }}
            >
                {label}
            </span>
            <span
                aria-hidden
                style={{
                    flex: 1,
                    minWidth: 48,
                    paddingBottom: 3,
                    borderBottom: '1px dotted #222',
                }}
            />
        </div>
    );
}

function FuelCardFormPreview({ form }, ref) {
    const { t } = useTranslation();
    const fuelTypeLabel = t(`fuelCardForm.fuelTypes.${form.fuelType}`);
    const d = buildFuelCardLetter(form, fuelTypeLabel);
    const attachments = t('fuelCardForm.preview.attachments', { returnObjects: true });

    return (
        <div ref={ref} data-fuel-card-page style={PAGE_STYLE}>
            <div style={PHOTO_BOX}>
                <span style={{ fontSize: 10, color: '#666', lineHeight: 1.4, fontWeight: 500 }}>
                    {t('fuelCardForm.preview.photoHint')}
                </span>
            </div>

            <div
                data-title-block
                style={{
                    paddingRight: 108,
                    textAlign: 'center',
                    boxSizing: 'border-box',
                }}
            >
                <div
                    style={{
                        fontWeight: 700,
                        fontSize: 14,
                        lineHeight: '20px',
                        textDecoration: 'underline',
                        textUnderlineOffset: 4,
                    }}
                >
                    {t('fuelCardForm.preview.title')}
                </div>
                <div
                    aria-hidden
                    data-title-gap
                    style={{
                        display: 'block',
                        width: '100%',
                        height: TITLE_BOTTOM_GAP_PX,
                        minHeight: TITLE_BOTTOM_GAP_PX,
                    }}
                />
            </div>

            <div style={{ marginBottom: 8 }}>
                <NumberedBlock number="১">
                    <FieldLine label={t('fuelCardForm.preview.applicantLabel')} value={d.applicantName} />
                </NumberedBlock>
                <NumberedBlock number="২">
                    <FieldLine label={t('fuelCardForm.preview.fatherLabel')} value={d.fatherOrProprietor} />
                </NumberedBlock>
                <NumberedBlock number="৩">
                    <FieldLine label={t('fuelCardForm.preview.villageLabel')} value={d.villageRoad} />
                </NumberedBlock>
                <NumberedBlock number="৪">
                    <FieldLine label={t('fuelCardForm.preview.unionLabel')} value={d.union} />
                </NumberedBlock>
                <NumberedBlock number="৫">
                    <FieldLine label={t('fuelCardForm.preview.postOfficeLabel')} value={d.postOffice} />
                </NumberedBlock>
                <NumberedBlock number="৬">
                    <FieldLine label={t('fuelCardForm.preview.thanaLabel')} value={d.thanaUpazila} />
                </NumberedBlock>
                <NumberedBlock number="৭">
                    <FieldLine label={t('fuelCardForm.preview.districtLabel')} value={d.district} />
                </NumberedBlock>
                <NumberedBlock number="৮">
                    <FieldLine label={t('fuelCardForm.preview.nidLabel')} value={d.nidOrLicense} />
                </NumberedBlock>
                <NumberedBlock number="৯">
                    <FieldLine label={t('fuelCardForm.preview.pumpLabel')} value={d.pumpName} />
                </NumberedBlock>
                <NumberedBlock number="১০">
                    <FieldLine label={t('fuelCardForm.preview.machineLabel')} value={d.machineName} />
                </NumberedBlock>
                <NumberedBlock number="১১">
                    <FieldLine label={t('fuelCardForm.preview.fuelTypeLabel')} value={d.fuelType} />
                </NumberedBlock>
                <NumberedBlock number="১২">
                    <FieldLine label={t('fuelCardForm.preview.fuelLitersLabel')} value={d.fuelLiters} />
                </NumberedBlock>
                <NumberedBlock number="১৩">
                    <FieldLine label={t('fuelCardForm.preview.purposeLabel')} value={d.purpose} />
                </NumberedBlock>
                <NumberedBlock number="১৪">
                    <FieldLine label={t('fuelCardForm.preview.mobileLabel')} value={d.mobile} />
                </NumberedBlock>
            </div>

            <div
                style={{
                    fontSize: BODY_FONT_PX,
                    marginBottom: 16,
                    textAlign: 'justify',
                    paddingRight: 8,
                    lineHeight: 1.55,
                }}
            >
                <span style={{ fontWeight: 700 }}>{t('fuelCardForm.preview.petition')}</span>{' '}
                {t('fuelCardForm.preview.petitionBody')}
            </div>

            <div style={{ fontSize: BODY_FONT_PX, fontWeight: 700, marginBottom: 6 }}>
                {t('fuelCardForm.preview.attachmentsTitle')}
            </div>
            <ol style={{ margin: 0, paddingLeft: 20, marginBottom: 16 }}>
                {Array.isArray(attachments) && attachments.map((item) => (
                    <li key={item} style={{ fontSize: BODY_FONT_PX, marginBottom: 4, lineHeight: 1.45 }}>
                        {item}
                    </li>
                ))}
            </ol>

            <div
                data-fuel-approval-box
                style={{
                    border: '1px solid #111',
                    padding: '14px 16px 16px',
                    marginTop: 4,
                    boxSizing: 'border-box',
                }}
            >
                <div
                    style={{
                        fontSize: BODY_FONT_PX,
                        fontWeight: 700,
                        textAlign: 'center',
                        marginBottom: 14,
                        lineHeight: 1.4,
                    }}
                >
                    {t('fuelCardForm.preview.approvalTitle')}
                </div>
                <OfficialDottedLine label={t('fuelCardForm.preview.approvedAmount')} />
                <OfficialDottedLine label={t('fuelCardForm.preview.officerSignature')} noMargin />
            </div>
        </div>
    );
}

export default forwardRef(FuelCardFormPreview);
